import {IRequestResModel, useGetRequestSearch} from "@/shared/api";
import {vars} from "@/shared/lib";
import {REQUEST_STATUS, REQUEST_TYPE} from "@/shared/model";
import {Column, Pagination, Row, Table, Typography} from "@/shared/ui";
import {createFileRoute, useNavigate} from "@tanstack/react-router";
import {RowSelectionState, createColumnHelper, flexRender, getCoreRowModel, useReactTable} from "@tanstack/react-table";
import {Suspense, useState} from "react";

export const Route = createFileRoute("/(auth)/_layout/approval/")({
	component: Approval
});

function Approval() {
	return (
		<Suspense>
			<ApprovalTable />
		</Suspense>
	);
}

const columnHelper = createColumnHelper<IRequestResModel>();

const columns = [
	columnHelper.accessor("requestId", {
		header: () => "",
		cell: (info) => (
			<Typography size={vars.size[14]} fontWeight="normal" inline>
				{info.getValue()}
			</Typography>
		),
		size: 120
	}),
	columnHelper.accessor("managerDepartment", {
		header: () => "영업부",
		cell: (info) => (
			<Typography size={vars.size[14]} fontWeight="normal" inline>
				{info.getValue()}
			</Typography>
		),
		size: 120
	}),
	columnHelper.accessor("managerName", {
		header: () => "영업 관리자",
		cell: (info) => (
			<Typography size={vars.size[14]} fontWeight="normal" inline>
				{info.getValue()}
			</Typography>
		),
		size: 120
	}),
	columnHelper.accessor("taxAccountantName", {
		header: () => "세무사명",
		cell: (info) => (
			<Typography size={vars.size[14]} fontWeight="normal" inline>
				{info.getValue()}
			</Typography>
		),
		size: 200
	}),
	columnHelper.accessor("requestType", {
		header: () => "요청내용",
		cell: (info) => {
			const value = info.getValue();
			return (
				<Typography size={vars.size[14]} fontWeight="normal" inline>
					{value ? REQUEST_TYPE[value] : value}
				</Typography>
			);
		},
		size: 200
	}),
	columnHelper.accessor("requestStatus", {
		header: () => "요청상태",
		cell: (info) => {
			const value = info.getValue();
			return (
				<Typography size={vars.size[14]} fontWeight="normal" inline>
					{value ? REQUEST_STATUS[value] : value}
				</Typography>
			);
		},
		size: 120
	}),
	columnHelper.accessor("requestedAt", {
		header: () => "요청일자",
		cell: (info) => (
			<Typography size={vars.size[14]} fontWeight="normal" inline>
				{info.getValue()}
			</Typography>
		),
		size: 120
	})
];

function ApprovalTable() {
	const navigate = useNavigate();

	const [currentPage, setCurrentPage] = useState<number>(0);
	const [pageSize, setPageSize] = useState<number>(20);
	const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

	const {data} = useGetRequestSearch({
		pageRequest: {
			currentPage: currentPage,
			pageSize: pageSize,
			sortType: "DESC"
		}
	});

	const table = useReactTable({
		data: data.content,
		columns: columns,
		initialState: {
			columnVisibility: {
				requestId: false
			}
		},
		state: {rowSelection},
		enableRowSelection: true,
		enableMultiRowSelection: true,
		onRowSelectionChange: setRowSelection,
		getCoreRowModel: getCoreRowModel(),

		debugTable: false,
		debugHeaders: false,
		debugColumns: false,
		debugCells: false,
		debugRows: false
	});

	const getVisiblePages = (page: number, total: number) => {
		const pageGroupSize = 5; // 한 번에 표시할 페이지 번호의 개수
		const currentGroup = Math.ceil(page / pageGroupSize); // 현재 페이지 그룹 계산
		const start = (currentGroup - 1) * pageGroupSize + 1; // 현재 그룹의 시작 페이지 번호
		const end = Math.min(start + pageGroupSize - 1, total); // 현재 그룹의 마지막 페이지 번호

		return Array.from({length: end - start + 1}, (_, index) => start + index);
	};

	const visiblePages = getVisiblePages(currentPage + 1, data.totalPages);

	return (
		<Column fullWidth style={{gap: vars.size[16]}}>
			<Row fullWidth style={{overflow: "auto", padding: vars.size[4]}}>
				<Table style={{width: "100%"}}>
					<Table.Header>
						{table.getHeaderGroups().map(({id, headers}) => (
							<Table.TR key={id}>
								{headers.map(({getContext, column, id}) => (
									<Table.TH key={id} style={{width: column.getSize()}} onClick={column.getToggleSortingHandler()}>
										{flexRender(column.columnDef.header, getContext())}
									</Table.TH>
								))}
							</Table.TR>
						))}
					</Table.Header>
					<Table.Body>
						{table.getRowModel().rows.map(({id, getVisibleCells, getValue}) => (
							<Table.TR
								key={id}
								onClick={() => {
									const requestId = getValue("requestId") as string;

									if (requestId) {
										navigate({
											to: "/approval/$requestId",
											params: {
												requestId: requestId
											}
										});
									}
								}}
							>
								{getVisibleCells().map(({id, column, getContext}) => (
									<Table.TD key={id} style={{width: column.getSize()}}>
										{flexRender(column.columnDef.cell, getContext())}
									</Table.TD>
								))}
							</Table.TR>
						))}
					</Table.Body>
				</Table>
			</Row>

			<Pagination
				totalRowCount={data.totalCount}
				visiblePages={visiblePages}
				nextPage={() => setCurrentPage((prev) => Math.min(prev + 1, data.totalPages - 1))}
				canNextPage={currentPage < data.totalPages - 1}
				previousPage={() => setCurrentPage((prev) => Math.max(prev - 1, 0))}
				canPreviousPage={currentPage > 0}
				currentPage={currentPage}
				firstPage={() => setCurrentPage(0)}
				lastPage={() => setCurrentPage(data.totalPages - 1)}
				setPageIndex={(pageIndex) => setCurrentPage(pageIndex)}
				pageSize={pageSize}
				setPageSize={(pageSize) => setPageSize(pageSize)}
			/>
		</Column>
	);
}
